import React, {useState, useEffect} from 'react';
import { Link, useParams } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Box from '@mui/material/Box';
import AddIcon from '@mui/icons-material/Add';
import { Button, Chip } from '@mui/material';
import Typography from '@mui/material/Typography';

import { adminGetTheoryQuestions, adminGetTheoryStage } from "../../api/Api";
import { useColors } from '../../theme/ColorModeContext';
import PageContainer from '../../shared/PageContainer';
import GlowCard from '../../shared/GlowCard';
import CyberpunkTitle from '../../shared/CyberpunkTitle';

interface PropsAdminTheoryQuestions {
    token: string;
}

interface TheoryQuestion {
    id: string;
    order_index: number;
    question: string;
    correct_choice: string;
}

const AdminTheoryQuestions = (props: PropsAdminTheoryQuestions) => {
    const colors = useColors();
    const { stage_id } = useParams();
    const [questions, setQuestions] = useState<TheoryQuestion[]>([]);
    const [stageTitle, setStageTitle] = useState('');
    const token = props.token;

    useEffect(() => {
        const fetchData = async () => {
            const [stage, data] = await Promise.all([
                adminGetTheoryStage(token, stage_id),
                adminGetTheoryQuestions(token, stage_id),
            ]);
            if (stage) setStageTitle(stage.title);
            if (data) setQuestions(data.questions);
        }
        fetchData();
    }, []);

    return (
        <PageContainer>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                <CyberpunkTitle
                    segments={[
                        { label: 'Theory Stages', to: '/admin/theory/stages' },
                        { label: stageTitle || 'Stage', to: `/admin/theory/stage/${stage_id}` },
                        { label: 'Questions' },
                    ]}
                    mb="0"
                />
                <Link to={`/admin/theory/stage/${stage_id}/questions/add`} style={{ textDecoration: 'none' }}>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        sx={{
                            borderColor: colors.accent.cyan,
                            color: colors.accent.cyan,
                            fontFamily: '"Share Tech Mono", monospace',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            letterSpacing: '0.12em',
                            textTransform: 'uppercase',
                            padding: '8px 20px',
                            borderRadius: '6px',
                        }}
                    >
                        Add Question
                    </Button>
                </Link>
            </Box>

            <GlowCard glowOnHover={false}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{
                                borderBottom: `1px solid ${colors.border.accent}`,
                                '& th': {
                                    fontFamily: '"Share Tech Mono", monospace',
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                    letterSpacing: '0.14em',
                                    textTransform: 'uppercase',
                                    color: colors.accent.cyan,
                                    borderBottom: 'none',
                                    padding: '14px 16px',
                                }
                            }}>
                                <TableCell align="right" sx={{ width: '60px' }}>Order</TableCell>
                                <TableCell>Question</TableCell>
                                <TableCell align="center">Correct</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {questions.sort((a, b) => a.order_index - b.order_index).map((q) => (
                                <TableRow
                                    key={q.id}
                                    sx={{
                                        '&:last-child td': { border: 0 },
                                        borderBottom: `1px solid ${colors.border.subtle}`,
                                        '& td': { borderBottom: 'none', padding: '12px 16px' },
                                    }}
                                >
                                    <TableCell align="right">
                                        <Typography sx={{ fontFamily: '"Share Tech Mono", monospace', fontSize: '0.75rem', color: colors.text.muted }}>
                                            {q.order_index}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Link style={{ textDecoration: 'none' }} to={`/admin/theory/question/${q.id}`}>
                                            <Typography sx={{
                                                color: colors.text.primary,
                                                '&:hover': { color: colors.accent.cyan },
                                                maxWidth: '520px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}>
                                                {q.question}
                                            </Typography>
                                        </Link>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={q.correct_choice.toUpperCase()}
                                            size="small"
                                            sx={{
                                                fontFamily: '"Share Tech Mono", monospace',
                                                fontWeight: 700,
                                                backgroundColor: colors.accent.cyanGlow,
                                                color: colors.accent.cyan,
                                                border: `1px solid ${colors.accent.cyan}44`,
                                                height: '22px',
                                            }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                {!questions.length && (
                    <Box sx={{ textAlign: 'center', padding: '60px 0' }}>
                        <Typography sx={{ color: colors.text.secondary }}>No questions yet for this stage.</Typography>
                    </Box>
                )}
            </GlowCard>
        </PageContainer>
    );
};

export default AdminTheoryQuestions;
